import { useEffect } from 'react';
import { useJWTAuth } from './JWTAuthContext';

/**
 * Custom hook to sync wallet authentication with JWT authentication
 * This hook should be used in components that need both wallet auth and JWT auth
 * Now also handles reverse sync: JWT -> wallet user
 */
export const useAccountJWTSync = (walletUser, setWalletUser) => {
  const { isAuthenticated, login, user: jwtUser } = useJWTAuth();

  useEffect(() => {
    const syncWithJWT = async () => {
      // Only sync if we have a wallet user and no JWT auth
      // Note: This is now secondary to the auto-login in EntitlementsContext
      if (walletUser && walletUser.wallet_address && !isAuthenticated()) {
        console.log('🔄 [AccountJWTSync] Syncing wallet auth with JWT for wallet:', walletUser.wallet_address);
        try {
          const result = await login(walletUser.wallet_address);
          if (result.success) {
            console.log('✅ [AccountJWTSync] JWT authentication successful');
          } else {
            console.log('❌ [AccountJWTSync] JWT authentication failed:', result.error);
          }
        } catch (error) {
          console.error('❌ [AccountJWTSync] Error syncing with JWT:', error);
        }
      }
    };

    // Small delay to allow EntitlementsContext auto-login to take precedence
    const timeoutId = setTimeout(syncWithJWT, 100);

    return () => clearTimeout(timeoutId);
  }, [walletUser, isAuthenticated, login]);

  // Reverse sync: when JWT is authenticated but wallet user is not set
  useEffect(() => {
    if (isAuthenticated() && jwtUser && !walletUser && setWalletUser) {
      console.log('🔄 [AccountJWTSync] Syncing JWT user to wallet user:', jwtUser);
      setWalletUser({
        id: jwtUser.id,
        username: jwtUser.username,
        wallet_address: jwtUser.wallet_address
      });
    }
  }, [isAuthenticated, jwtUser, walletUser, setWalletUser]);

  return {
    isJWTAuthenticated: isAuthenticated
  };
};
